/*
 * home (page)
 */
import {
	Badge,
	ButtonLink,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Container,
	Page,
} from "@psychology/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { BrainCircuit, MessageSquare, Zap } from "lucide-react"
import { appConfig } from "../config/app-config"
import { authPageLoaderSoft } from "../utils/auth.utils"

export const Route = createFileRoute("/")({
	component: HomePage,
	loader: authPageLoaderSoft({
		allowUnauthenticated: true,
		redirectAuthorized: appConfig.redirects.afterSignInUrl,
	}),
})

const features = [
	{
		icon: BrainCircuit,
		title: "Powerful AI Models",
		description:
			"Access leading AI models through a single, unified interface. Switch models or configure behavior without touching code.",
	},
	{
		icon: MessageSquare,
		title: "Conversational AI",
		description:
			"Chat with AI in real time. Persistent conversation history, streaming responses, and full context awareness.",
	},
	{
		icon: Zap,
		title: "Built for Speed",
		description:
			"Serverless infrastructure that scales instantly. No cold-start lag, no maintenance overhead — just fast, reliable responses.",
	},
]

function HomePage() {
	const { authenticated } = Route.useLoaderData()
	const waitlistHref = authenticated ? "/waitlist" : "/auth?tab=register"

	return (
		<Page centerVertical={false}>
			<Container width={"xxxl"} padding={"lg"} gap={"lg"}>
				{/* Hero */}
				<section
					className={
						"w-full flex flex-col items-center py-24 px-4 gap-6 text-center"
					}
				>
					<Badge variant={"outline"} className={"text-sm px-4 py-1"}>
						Early Access
					</Badge>
					<h1 className={"text-5xl font-bold tracking-tight max-w-2xl"}>
						AI services, ready to use.
					</h1>
					<p className={"text-muted-foreground text-lg max-w-xl"}>
						Psychology gives you direct access to powerful AI tools — chat,
						generation, and more — through one clean platform. No setup. No
						complexity.
					</p>
					<div className={"flex flex-col sm:flex-row gap-3 mt-2"}>
						<ButtonLink to={waitlistHref} size={"lg"}>
							Get Early Access
						</ButtonLink>
					</div>
				</section>
				<section className={"w-full flex justify-center px-4 pb-24"}>
					<div className={"grid grid-cols-1 sm:grid-cols-3 gap-6"}>
						{features.map(({ icon: Icon, title, description }) => (
							<Card key={title} className={"bg-background/60 backdrop-blur-sm"}>
								<CardHeader className={"gap-3"}>
									<div
										className={
											"w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"
										}
									>
										<Icon className={"w-5 h-5 text-primary"} />
									</div>
									<CardTitle className={"text-base"}>{title}</CardTitle>
								</CardHeader>
								<CardContent>
									<p className={"text-sm text-muted-foreground"}>
										{description}
									</p>
								</CardContent>
							</Card>
						))}
					</div>
				</section>
			</Container>
		</Page>
	)
}
